import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Input, toast } from '@utima/ui';
import { Check, Pencil, X } from 'lucide-react';
import { useState } from 'react';

import { trpc } from '@/services/trpc';

interface StripeCustomerIdFieldProps {
  organizationId: string;
  currentValue: string | null;
  onUpdate: () => void;
}

export function StripeCustomerIdField({
  organizationId,
  currentValue,
  onUpdate,
}: StripeCustomerIdFieldProps) {
  const { t } = useLingui();
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(currentValue ?? '');

  const linkMutation = trpc.organizations.linkStripeCustomer.useMutation({
    onSuccess: data => {
      toast.success(
        data.reconciled
          ? t`Stripe Customer ID updated and subscription synced.`
          : t`Stripe Customer ID updated successfully.`,
      );
      setIsEditing(false);
      setInputValue(data.stripeCustomerId ?? '');
      onUpdate();
    },
    onError: (error: { message: string }) => {
      toast.error(t`Failed to update Stripe Customer ID: ${error.message}`);
    },
  });

  const handleSave = () => {
    const trimmed = inputValue.trim();

    if (trimmed && !trimmed.startsWith('cus_')) {
      toast.error(t`Stripe Customer ID must start with "cus_".`);

      return;
    }

    if (!trimmed && currentValue) {
      const confirmed = window.confirm(
        t`Do you really want to unlink this Stripe Customer ID?`,
      );

      if (!confirmed) {
        return;
      }

      linkMutation.mutate({
        id: organizationId,
        stripeCustomerId: null,
        confirmUnlink: true,
      });

      return;
    }

    linkMutation.mutate({
      id: organizationId,
      stripeCustomerId: trimmed || null,
      confirmUnlink: false,
    });
  };

  const handleCancel = () => {
    setInputValue(currentValue ?? '');
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className='flex items-center gap-2'>
        <span className='text-sm font-medium'>
          <Trans>Stripe Customer ID:</Trans>
        </span>
        <Input
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          placeholder='cus_...'
          className='max-w-xs'
          disabled={linkMutation.isPending}
        />
        <Button
          variant='ghost'
          size='sm'
          onClick={handleSave}
          disabled={linkMutation.isPending}
          aria-label={t`Save`}
        >
          <Check className='size-4' />
        </Button>
        <Button
          variant='ghost'
          size='sm'
          onClick={handleCancel}
          disabled={linkMutation.isPending}
          aria-label={t`Cancel`}
        >
          <X className='size-4' />
        </Button>
      </div>
    );
  }

  return (
    <div className='flex items-center gap-2'>
      <span className='text-sm font-medium'>
        <Trans>Stripe Customer ID:</Trans>
      </span>
      {currentValue ? (
        <code className='rounded bg-muted px-2 py-0.5 font-mono text-xs'>
          {currentValue}
        </code>
      ) : (
        <span className='text-sm font-medium text-amber-600'>
          <Trans>Not linked</Trans>
        </span>
      )}
      <Button
        variant='ghost'
        size='icon-xs'
        aria-label={t`Edit Stripe Customer ID`}
        onClick={() => setIsEditing(true)}
      >
        <Pencil className='size-3.5' />
      </Button>
    </div>
  );
}
